import {
  Box,
  Typography,
  Grid,
  Divider,
  Stack,
  useTheme,
  Button,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { encodeId } from '../utils/idEncoder';

type Dealer = {
  dealerCompanyId: number;
  dealerCompanyName: string;
  imageUrl: string;

};

type Props = {
  dealerData: Dealer[];
};

const DealerListGrouped = ({ dealerData }: Props) => {
  const navigate = useNavigate();
  const [groupedDealers, setGroupedDealers] = useState<Record<string, Dealer[]>>({});
  const theme = useTheme();
  const [activeGroup, setActiveGroup] = useState<string | null>(null);

  const groupKeys = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

  useEffect(() => {
    const groups: Record<string, Dealer[]> = {};

    dealerData.forEach((dealer) => {
      const firstChar = dealer.dealerCompanyName.charAt(0).toUpperCase();
      const groupKey = /^[A-Z]$/.test(firstChar) ? firstChar : '#';

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(dealer);
    });

    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => a.dealerCompanyName.localeCompare(b.dealerCompanyName));
    });

    const sortedGroups: Record<string, Dealer[]> = {};
    groupKeys.forEach((key) => {
      if (groups[key]) {
        sortedGroups[key] = groups[key];
      }
    });

    setGroupedDealers(sortedGroups);
  }, [dealerData]);

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 5}, py: 4 }}>
      {/* --- Alphabetical Indicator --- */}
      <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" mb={4}>
        {groupKeys.map((key) => (
          <Button
            key={key}
            size="small"
            variant={activeGroup === key ? 'contained' : 'outlined'}
            onClick={() => {
              const element = document.getElementById(`group-${key}`);
              element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              setActiveGroup(key);
            }}
            sx={{ minWidth: 32, px: 1, py: 0.5 }}
          >
            {key}
          </Button>
        ))}
      </Stack>

      {/* --- Grouped Dealer Cards --- */}
      {Object.entries(groupedDealers).map(([group, dealers]) => (
        <Box key={group} sx={{ mb: 6 }} id={`group-${group}`}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {group}
            </Typography>
            <Divider sx={{ flex: 1, borderColor: theme.palette.divider }} />
          </Stack>

          <Grid container spacing={2}>
            {dealers.map((dealer) => (
              <Grid size={{xs:6, sm:4, md:3, lg:2}} key={dealer.dealerCompanyId}>
                <Box
                  onClick={() => navigate(`/dealers/${encodeId(dealer.dealerCompanyId)}`)}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 3,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" width="100%">
                    <Box
                      component="img"
                      src={dealer.imageUrl}
                      alt={dealer.dealerCompanyName}
                      sx={{
                        width: 40,
                        height: 30,
                        objectFit: 'contain',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.85rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {dealer.dealerCompanyName}
                    </Typography>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default DealerListGrouped;
